﻿import React, { createElement, useContext } from "react";
import ReactDOM from "react-dom";

import { DateSelection } from "./form-date";
import moment from "moment";
import { products } from "../../../Components/Pages/BookingFlowPage/Scripts/Endpoints";
import controller from "./controller";
import "./fileupload";
import "./form-license-plate";
import { pushToDataLayer } from "./dataLayer";

// ---------------------------------------------------------------------------
// Multi-step form field persistence across page refreshes
// ---------------------------------------------------------------------------

const FORM_SESSION_PREFIX = "epiForm_";

function formFieldKey(formId, name) {
    return `${FORM_SESSION_PREFIX}${formId}_${name}`;
}

function isPersistedField(input) {
    // EPiServer marks every user-created form field with data-f-datainput.
    if (!input.hasAttribute("data-f-datainput")) return false;
    if (input.type === "hidden" || input.type === "file") return false;
    if (input.tagName === "SELECT") return false; // handled by accessible-select
    return true;
}

function persistField(formId, input) {
    if (!isPersistedField(input)) return;
    const key = formFieldKey(formId, input.name);
    if (input.type === "radio" || input.type === "checkbox") {
        if (input.checked) sessionStorage.setItem(key, input.value);
        else if (input.type === "checkbox") sessionStorage.removeItem(key);
    } else {
        if (input.value) sessionStorage.setItem(key, input.value);
        else sessionStorage.removeItem(key);
    }
}

function restoreStepFields(form) {
    const formId = form.id;

    form.querySelectorAll(
        "input[data-f-datainput]:not([type=hidden]):not([type=file]), textarea[data-f-datainput]",
    ).forEach((input) => {
        if (!isPersistedField(input)) return;
        const saved = sessionStorage.getItem(formFieldKey(formId, input.name));
        if (!saved) return;

        if (input.type === "radio") {
            const groupChecked = Array.from(
                form.querySelectorAll(
                    `input[type="radio"][name="${input.name}"]`,
                ),
            ).some((r) => r.checked);
            if (!groupChecked && input.value === saved) input.checked = true;
        } else if (input.type === "checkbox") {
            if (!input.checked) input.checked = saved === input.value;
        } else {
            // Always apply the saved value — the user's explicit input
            input.value = saved;
        }
    });
}

function clearFormSession(formId) {
    const prefix = `${FORM_SESSION_PREFIX}${formId}_`;
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) toRemove.push(key);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
}

function findEmailInSession(formId) {
    const prefix = `${FORM_SESSION_PREFIX}${formId}_`;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) {
            const value = sessionStorage.getItem(key);
            if (value && emailRegex.test(value)) return value;
        }
    }
    return null;
}

async function sha256(str) {
    const buf = new TextEncoder().encode(str.toLowerCase().trim());
    const hash = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

document.addEventListener("DOMContentLoaded", () => {
    // Push Enhanced Conversions event on any page that follows a form submission.
    // The email is stored in sessionStorage right before form.submit() so this
    // works regardless of whether the form shows a success message in-place or
    // redirects to a separate thank-you page.
    const pendingEmail = sessionStorage.getItem("ec_pending_email");
    if (pendingEmail) {
        sessionStorage.removeItem("ec_pending_email");
        sha256(pendingEmail).then((hashedEmail) => {
            pushToDataLayer({
                event: "generate_lead",
                user_data: {
                    email_address: pendingEmail,
                    sha256_email_address: hashedEmail,
                },
            });
        });
    }

    // Select the main EPiServer form on the page
    const form = document.querySelector("form.EPiServerForms");

    if (form) {
        const formId = form.id;

        // Clear stored data if the user has completed the form.
        const isFinalized =
            form.querySelector("[data-f-form-successmessage]") ||
            form.querySelector(".Form__Status__Message.Form__Success__Message");

        if (isFinalized) {
            clearFormSession(formId);
        } else {
            restoreStepFields(form);
            requestAnimationFrame(() => restoreStepFields(form));
        }

        // Save field values as the user types / makes selections.
        form.addEventListener("input", (e) => persistField(formId, e.target));
        form.addEventListener("change", (e) => persistField(formId, e.target));

        // Clear saved data when the Reset button is used.
        form.addEventListener("reset", () => clearFormSession(formId));

        // Fetch a fresh anti-forgery token from the server
        // This ensures the token matches the currently logged-in EPiServer identity
        fetch("/AntiForgery/GetToken/")
            .then((r) => r.text())
            .then((html) => {
                const div = document.createElement("div");
                div.innerHTML = html;

                // Extract the new token from the server response
                const token = div.querySelector(
                    'input[name="__RequestVerificationToken"]',
                ).value;

                // Update the form's hidden anti-forgery token before submission
                form.querySelector(
                    'input[name="__RequestVerificationToken"]',
                ).value = token;
            });
    }

    // Iterate over all multi-step form bodies
    Array.prototype.slice
        .call(document.querySelectorAll("form .Form__MainBody"))
        .forEach((formBodyElement) => {
            // Attach submit handler that refreshes the anti-forgery token
            formBodyElement
                .closest("form")
                .addEventListener("submit", function (e) {
                    e.preventDefault();

                    // Fetch a new token on submit to avoid 500 error after login
                    fetch("/AntiForgery/GetToken/")
                        .then((r) => r.text())
                        .then((html) => {
                            const div = document.createElement("div");
                            div.innerHTML = html;

                            const newToken = div.querySelector(
                                'input[name="__RequestVerificationToken"]',
                            ).value;

                            const form = formBodyElement.closest("form");
                            form.querySelector(
                                'input[name="__RequestVerificationToken"]',
                            ).value = newToken;

                            // Capture email for Enhanced Conversions before navigating away.
                            const emailInput = Array.from(
                                form.querySelectorAll("input[data-f-datainput]"),
                            ).find((inp) =>
                                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inp.value),
                            );
                            if (emailInput && emailInput.value) {
                                sessionStorage.setItem(
                                    "ec_pending_email",
                                    emailInput.value,
                                );
                            }

                            // Submit the real form with the updated token
                            form.submit();
                        });
                });

            var selectCount = 0;
            var fileUploadCount = 0;

            // Iterate through each step of the multi-step form
            Array.prototype.slice
                .call(
                    formBodyElement.querySelectorAll(
                        "form .Form__MainBody .FormStep",
                    ),
                )
                .forEach((formStepElement) => {
                    //Radiobuttons and Checkboxes
                    const selectButtonListItems = Array.prototype.slice.call(
                        formStepElement.querySelectorAll(
                            "form .Form__Element.FormChoice fieldset label",
                        ),
                    );
                    if (selectButtonListItems.length > 0) {
                        selectButtonListItems.forEach((listItem) => {
                            listItem.appendChild(
                                document.createElement("SPAN"),
                            );
                        });
                    }

                    //Textarea
                    const textareaItems = Array.prototype.slice.call(
                        formStepElement.querySelectorAll("textarea"),
                    );
                    const urlParams = new URLSearchParams(
                        window.location.search,
                    );
                    const pmcName = urlParams.get("p");
                    if (textareaItems.length > 0 && pmcName)
                        textareaItems[0].value = pmcName;

                    //Dropdown lists
                    const selectContainerItems = Array.prototype.slice.call(
                        formStepElement.querySelectorAll(
                            "form .Form__Element.FormSelection select",
                        ),
                    );
                    if (selectContainerItems.length > 0) {
                        selectContainerItems.forEach((selectItem) => {
                            selectItem.setAttribute(
                                "data-controller",
                                "accessible-select",
                            );
                            selectCount++;
                        });
                    }

                    //File upload
                    const fileuploadContainerItems = Array.prototype.slice.call(
                        formStepElement.querySelectorAll(
                            "form .Form__Element.FormFileUpload",
                        ),
                    );
                    if (fileuploadContainerItems.length > 0) {
                        fileuploadContainerItems.forEach((fileupload) => {
                            fileupload.setAttribute(
                                "data-controller",
                                "accessible-fileupload",
                            );
                            fileUploadCount++;
                        });
                    }

                    //Datepicker and Timepicker
                    const addDatepicker = (singleDatepickerWrapper) => {
                        const existingPicker =
                            singleDatepickerWrapper.querySelector(
                                '.Form__CustomInput[class*="FormDateTime"]',
                            ) || singleDatepickerWrapper;
                        existingPicker.classList.add("hide");
                        const validationErrorSpans =
                            singleDatepickerWrapper.parentNode.querySelectorAll(
                                ".Form__Element__ValidationError",
                            );
                        const validationErrorSpansArray = validationErrorSpans
                            ? Array.prototype.slice.call(validationErrorSpans)
                            : [];
                        validationErrorSpansArray.forEach((span) =>
                            span.removeAttribute("style"),
                        );
                        const scriptTag =
                            singleDatepickerWrapper.querySelector("SCRIPT") ||
                            singleDatepickerWrapper.parentNode.querySelector(
                                "SCRIPT",
                            );
                        const currentCountry = document
                            .querySelector("html")
                            .getAttribute("country");
                        const currentLanguage = document
                            .querySelector("html")
                            .getAttribute("lang");

                        if (
                            scriptTag &&
                            scriptTag.innerText.includes("timepicker")
                        ) {
                            const visibleInput =
                                document.createElement("INPUT");
                            visibleInput.classList.add("FormTextbox__Input");
                            visibleInput.setAttribute("type", "time");
                            const placeholderText =
                                currentCountry == "se"
                                    ? currentLanguage == "sv"
                                        ? "Välj tiden"
                                        : "Choose time"
                                    : "Choose time";
                            visibleInput.setAttribute(
                                "placeholder",
                                placeholderText,
                            );
                            existingPicker.after(visibleInput);
                            visibleInput.addEventListener("change", (e) => {
                                existingPicker.value = e.target.value
                                    .toLowerCase()
                                    .includes("m")
                                    ? e.target.value
                                    : moment(e.target.value, ["HH.mm"]).format(
                                          "hh:mm A",
                                      );
                            });
                        } else {
                            const appContainer = document.createElement("DIV");
                            appContainer.classList.add(
                                "Form__DatePickerWrapper",
                            );
                            insertAfter(appContainer, existingPicker);

                            const tooltip =
                                existingPicker.getAttribute("title") ?? "";
                            ReactDOM.render(
                                <DateSelection
                                    existingPicker={existingPicker}
                                    locale={currentCountry}
                                    title={tooltip}
                                    showAllDates={true}
                                />,
                                appContainer,
                            );
                        }
                    };

                    const datepickerWrappers = Array.prototype.slice.call(
                        formStepElement.querySelectorAll(
                            "form .Form__CustomElement.FormDateTime",
                        ),
                    );
                    const datepickerRangeFieldsets = Array.prototype.slice.call(
                        formStepElement.querySelectorAll(
                            "form .Form__CustomElement.FormDateTimeRange",
                        ),
                    );
                    if (datepickerWrappers.length > 0) {
                        datepickerWrappers.forEach(
                            (singleDatepickerWrapper) => {
                                addDatepicker(singleDatepickerWrapper);
                            },
                        );
                    }
                    if (datepickerRangeFieldsets.length > 0) {
                        datepickerRangeFieldsets.forEach(
                            (singleDatepickerFieldset) => {
                                const datetimeRangeInputs =
                                    Array.prototype.slice.call(
                                        singleDatepickerFieldset.querySelectorAll(
                                            "input",
                                        ),
                                    );
                                datetimeRangeInputs.forEach((input) => {
                                    addDatepicker(input);
                                });
                            },
                        );
                    }
                });

            if (selectCount > 0 || fileUploadCount > 0)
                controller.parse(formBodyElement);
        });
});

/* Add wrapper for Reset and Submit at the last step of the multi step form */
const wrapButtons = () => {
    const visibleSteps = document.querySelectorAll(".FormStep:not(.hide)");
    const lastStep = visibleSteps[visibleSteps.length - 1];

    if (!lastStep) return;

    if (lastStep.querySelector(".Form__ButtonWrapper")) return;

    const actionButtons = lastStep.querySelectorAll(
        'button[type="submit"]:not(.btnNext):not(.btnPrev), input[type="reset"]',
    );

    if (actionButtons.length === 0) return;

    const wrapper = document.createElement("div");
    wrapper.classList.add("Form__ButtonWrapper");

    actionButtons.forEach((btn) => wrapper.appendChild(btn));

    lastStep.appendChild(wrapper);
};

wrapButtons();

const observer = new MutationObserver(() => {
    wrapButtons();
});

const formBody = document.querySelector(".Form__MainBody");

if (formBody) {
    observer.observe(formBody, {
        attributes: true,
        subtree: true,
        attributeFilter: ["class"], // detects hide/remove hide
    });
}

document.addEventListener(
    "submit",
    function (e) {
        const form = e.target;

        if (form.matches(".EPiServerForms")) {
            // stops AJAX EPiServer
            form.removeAttribute("data-f-ajax");

            // force classic submit
            e.stopImmediatePropagation();
        }
    },
    true,
);
const initCustomDropDownLists = function (wrapper, selElmnt) {
    /* For each element, create a new DIV that will act as the selected item: */
    const selectedItem = document.createElement("DIV");
    selectedItem.classList.add("Form__FormSelectWrapper__Selector");
    selectedItem.innerHTML = selElmnt.options[selElmnt.selectedIndex].innerHTML;
    wrapper.appendChild(selectedItem);

    /* For each element, create a new DIV that will contain the option list: */
    const selectItems = document.createElement("DIV");
    selectItems.classList.add("Form__FormSelectWrapper__SelectItems");
    selectItems.classList.add("Form__FormSelectWrapper__SelectItems--hide");

    for (var j = 1; j < selElmnt.length; j++) {
        const singleSelectItem = document.createElement("DIV");
        singleSelectItem.innerHTML = selElmnt.options[j].innerHTML;

        singleSelectItem.addEventListener("click", function (e) {
            /* When an item is clicked, update the original select box,
        and the selected item: */
            var s =
                this.parentNode.parentNode.getElementsByTagName("select")[0];
            var h = this.parentNode.previousSibling;

            for (var i = 0; i < s.length; i++) {
                if (s.options[i].innerHTML === this.innerHTML) {
                    s.selectedIndex = i;
                    h.innerHTML = this.innerHTML;
                    var y =
                        this.parentNode.getElementsByClassName(
                            "same-as-selected",
                        );
                    for (var k = 0; k < y.length; k++) {
                        y[k].removeAttribute("class");
                    }
                    this.setAttribute("class", "same-as-selected");
                    break;
                }
            }
            h.click();
        });

        selectItems.appendChild(singleSelectItem);
    }

    wrapper.appendChild(selectItems);

    selectedItem.addEventListener("click", function (e) {
        /* When the select box is clicked, close any other select boxes,
    and open/close the current select box: */
        e.stopPropagation();
        closeAllSelect(this);
        this.nextSibling.classList.toggle(
            "Form__FormSelectWrapper__SelectItems--hide",
        );
        this.classList.toggle("Form__FormSelectWrapper__Selector--active");
    });
    //}
};

const closeAllSelect = function (elmnt) {
    /* A function that will close all select boxes in the document,
  except the current select box: */
    var x,
        y,
        i,
        arrNo = [];
    x = document.getElementsByClassName("Form__FormSelectWrapper__SelectItems");
    y = document.getElementsByClassName("Form__FormSelectWrapper__Selector");
    for (i = 0; i < y.length; i++) {
        if (elmnt === y[i]) {
            arrNo.push(i);
        } else {
            y[i].classList.remove("Form__FormSelectWrapper__Selector--active");
        }
    }
    for (i = 0; i < x.length; i++) {
        if (arrNo.indexOf(i)) {
            x[i].classList.add("Form__FormSelectWrapper__SelectItems--hide");
        }
    }
};

const insertAfter = function (newNode, referenceNode) {
    referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
};
